import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Users, CheckCircle2, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

/**
 * Dialog for managing team assignment (add/remove housekeepers).
 * Shows currently assigned housekeepers pre-checked.
 */
const ReassignDialog = ({
  open,
  onOpenChange,
  task,
  availableHousekeepers,
  loadingHousekeepers,
  selectedNewHousekeepers,
  toggleHousekeeperSelection,
  onReassign,
  actionLoading
}) => {
  const formatTaskDate = () => {
    if (!task?.date) return 'Date non définie';
    try {
      return format(new Date(task.date), "EEEE d MMMM yyyy", { locale: fr });
    } catch (e) {
      return task.date;
    }
  };

  // Get current assigned IDs
  const currentIds = task?.housekeeper_ids || (task?.housekeeper_id ? [task.housekeeper_id] : []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-500" />
            Gérer l'équipe
          </DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          {/* Current assignment info */}
          {task && (
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-sm text-slate-600">
                <strong>Prestation du</strong> {formatTaskDate()}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                {task.property_name}
              </p>
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">Sélectionnez 1 ou 2 femme(s) de ménage :</p>
            {selectedNewHousekeepers.length > 0 && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                {selectedNewHousekeepers.length}/2 sélectionnée(s)
              </span>
            )}
          </div>
          
          {/* Selected housekeepers preview */}
          {selectedNewHousekeepers.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-600 mb-2 font-medium">Équipe sélectionnée :</p>
              <div className="flex flex-wrap gap-2">
                {selectedNewHousekeepers.map(hk => (
                  <span key={hk.id} className="inline-flex items-center gap-1 bg-white border border-blue-300 rounded-full px-3 py-1 text-sm">
                    {hk.name}
                    {currentIds.includes(hk.id) && (
                      <Badge className="ml-1 bg-green-100 text-green-700 text-xs px-1 py-0">actuel</Badge>
                    )}
                    <button 
                      type="button" 
                      onClick={() => toggleHousekeeperSelection(hk)}
                      className="text-blue-500 hover:text-red-500 ml-1"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              {selectedNewHousekeepers.length === 2 && (
                <p className="text-xs text-amber-600 mt-2">⚠️ Mission 50/50 : la rémunération sera divisée entre les 2 FDM</p>
              )}
            </div>
          )}
          
          {/* Housekeepers list */}
          {loadingHousekeepers ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          ) : availableHousekeepers.length === 0 ? (
            <div className="text-center py-6 text-slate-500">
              <Users className="h-10 w-10 mx-auto mb-2 text-slate-300" />
              <p>Aucune FDM disponible</p>
            </div>
          ) : (
            <div className="grid gap-2 max-h-[300px] overflow-y-auto">
              {availableHousekeepers.map((hk) => {
                const isSelected = selectedNewHousekeepers.some(h => h.id === hk.id);
                const isCurrentlyAssigned = currentIds.includes(hk.id);
                return (
                  <button
                    key={hk.id}
                    type="button"
                    onClick={() => toggleHousekeeperSelection(hk)}
                    className={`w-full p-3 rounded-xl border-2 text-left flex items-center gap-3 transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                    }`}
                  >
                    {/* Avatar */}
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg overflow-hidden flex-shrink-0 ${
                      isCurrentlyAssigned 
                        ? 'bg-gradient-to-br from-green-500 to-emerald-600' 
                        : 'bg-gradient-to-br from-blue-500 to-blue-600'
                    }`}>
                      {hk.avatar_url ? (
                        <img src={hk.avatar_url} alt={hk.name} className="w-full h-full object-cover" />
                      ) : (
                        hk.name?.charAt(0)?.toUpperCase() || '?'
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-800 truncate">{hk.name}</p>
                        {isCurrentlyAssigned && (
                          <Badge className="bg-green-100 text-green-700 text-xs">Assignée</Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">{hk.email}</p>
                    </div>
                    
                    {isSelected && (
                      <CheckCircle2 className={`h-6 w-6 flex-shrink-0 ${isCurrentlyAssigned ? 'text-green-500' : 'text-blue-500'}`} />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <DialogFooter className="flex flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="sm:flex-1">
            Annuler
          </Button>
          <Button 
            className="bg-blue-600 hover:bg-blue-700 sm:flex-1" 
            onClick={onReassign} 
            disabled={actionLoading || selectedNewHousekeepers.length === 0}
            data-testid="confirm-reassign-btn"
          >
            {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {selectedNewHousekeepers.length === 0 
              ? 'Sélectionnez une FDM'
              : 'Valider l\'équipe'
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReassignDialog;
